import { FC, useEffect, useMemo, useState } from 'react';
import { useFm } from '@web3/base-hooks';
import cls from 'classnames';
import { Table } from '@bfe/moly';
import {
  formatKMB,
  formatTrend2 as formatTrend1,
  gt,
  lt,
  Chain,
  normalizeOutLink,
  replaceBybitToLocationHost,
} from '@web3/base-utils';
import { scTrack } from '@web3/base-tracking';
import { useHomeStore } from '@/store/home';
import { mainAddress } from '@/utils/constant';
import { formatDexPrice } from '@/utils/number';
import { Event, Page } from '@/config/track-config';

import styles from './index.module.scss';
import DefaultPic from '@/assets/icon/default_pic.svg';

export const TableData: FC = () => {
  const fm = useFm();
  const { tokens, chains } = useHomeStore();
  const [chainMap, setChainMap] = useState<Record<string, Chain>>({});

  useEffect(() => {
    const map: Record<string, Chain> = {}; // Specify the type of the map variable
    chains?.forEach((item) => {
      map[item.chainId] = { ...item };
    });
    setChainMap(map);
  }, [chains]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'tokenName',
        // @ts-ignore
        header: (
          <div className={cls(styles.headCell, styles.token)}>{fm('coin')}</div>
        ),
        cell: ({ row }) => {
          const tickName: string = row.getValue('tokenName');
          const link = replaceBybitToLocationHost(row.original.icon);
          const chainId = row.original.chainId;
          const chainItem = chainMap[chainId];
          return (
            <div className={styles.bodyCell}>
              <span className={styles.group}>
                {link ? (
                  <img className={styles.icon} alt="img" src={link} />
                ) : (
                  <DefaultPic className={styles.icon} />
                )}
                {chainItem?.iconNightUrl ? (
                  <img
                    className={styles.chainIcon}
                    alt="img"
                    src={chainItem?.iconNightUrl}
                  />
                ) : null}
              </span>
              <span className={styles.token}>{tickName}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'volume',
        // @ts-ignore
        header: (
          <div className={cls(styles.headCell, styles.volume, styles.left)}>
            {fm('volume24H')}
          </div>
        ),
        cell: ({ row }) => {
          const tokenVolume24h: string = row.getValue('volume');
          const volume = tokenVolume24h
            ? `$${formatKMB(tokenVolume24h)}`
            : '--';
          return (
            <div className={cls(styles.bodyCell, styles.left)}>{volume}</div>
          );
        },
      },
      {
        accessorKey: 'price',
        // @ts-ignore
        header: (
          <div className={cls(styles.headCell, styles.price, styles.left)}>
            {fm('price')}
          </div>
        ),
        cell: ({ row }) => {
          const tokenPrice: string = row.getValue('price');
          const price = tokenPrice ? `$${formatDexPrice(tokenPrice)}` : '--';
          return (
            <div className={cls(styles.bodyCell, styles.left)}>{price}</div>
          );
        },
      },
      {
        accessorKey: 'priceChange',
        // @ts-ignore
        header: (
          <div className={cls(styles.headCell, styles.others, styles.right)}>
            {fm('change24H')}
          </div>
        ),
        cell: ({ row }) => {
          const data: string = row.getValue('priceChange');
          const change = formatTrend1(data);
          const isPos = gt(data, 0);
          const isNeg = lt(data, 0);
          return (
            <div
              className={cls(styles.bodyCell, styles.right, {
                [styles.green]: isPos,
                [styles.red]: isNeg,
              })}
            >
              {change}
            </div>
          );
        },
      },
    ],
    [chainMap, fm]
  );

  const hotIconLink =
    'https://s1.bycsi.com/bybit/deadpool/image-43ff3e4b1c0b4d31ae5aedf03d41af0d.svg';

  const handleRowClick = (row, idx) => {
    scTrack(Event.Web3UserClick, {
      page_name: Page.Home,
      area: 'HotCoins',
      button_name: 'HotCoins',
      params: row?.tickName,
    });
    const item = (tokens || [])[idx];
    if (item) {
      const chainCode = chainMap[item.chainId]?.chainCode?.toLowerCase();
      const address =
        item.address !== mainAddress // 链上主币，不需要传链
          ? `/${item.address}`
          : '';
      const link = `/web3/dex/${chainCode}${address}`;
      window.open(normalizeOutLink(link));
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <img className={styles.icon} alt="img" src={hotIconLink} />
        <div className={styles.title}>{fm('hotCoins')}</div>
      </div>
      <div className={styles.table}>
        <Table
          columns={columns}
          data={tokens || []}
          onRowClick={handleRowClick}
        />
      </div>
    </div>
  );
};

export default TableData;
